import type { AssistantReportDefinition } from '@triedge/domain-core';
import {
  defineRtkPreloadOperation,
  type RtkPreloadEndpoint,
  type RtkPreloadOperationDescriptor,
  type RtkPreloadRuntimeContext,
} from './index';

export const RUN_REPORTS_MODULE_ID = 'run-reports' as const;
export const RUN_REPORTS_PATHNAME = '/school/run-reports' as const;
export const RUN_REPORTS_ROUTE_FAMILY = 'tuition' as const;

export const RUN_REPORTS_PRELOAD_OPERATION_IDS = [
  'run-reports.getAssistantReportGenerationWorkspace.bootstrap',
  'run-reports.listAssistantWorkItems.nextPage',
] as const;

export type RunReportsPreloadOperationId =
  typeof RUN_REPORTS_PRELOAD_OPERATION_IDS[number];

export type RunReportsDomainQueryOperation =
  | 'getAssistantReportGenerationWorkspace'
  | 'listAssistantWorkItems'
  | 'previewAssistantReportGeneration';

export type RunReportsDomainQueryRequest = {
  operation: RunReportsDomainQueryOperation;
  args: Record<string, unknown>;
};

export type RunReportsPreloadCachePolicy = {
  maxAgeMs?: number;
  forceRefresh?: boolean;
  staleRefresh?: boolean;
  staleWhileRevalidate?: boolean;
};

export type BuildRunReportsPreloadOperationsOptions<
  Context extends RtkPreloadRuntimeContext,
  Args,
  State,
> = {
  endpoint: RtkPreloadEndpoint<Args, State>;
  endpointIdPrefix: string;
  enabled: (context: Context) => boolean | Promise<boolean>;
  readRouteParam: (context: Context, key: string) => string | null;
  pageSize: number;
  listLimit?: number;
  previewLimit?: number;
  eagerCachePolicy?: RunReportsPreloadCachePolicy;
  mapRequest?: (request: RunReportsDomainQueryRequest) => Args;
};

const DEFAULT_LIST_LIMIT = 100;
const DEFAULT_PREVIEW_LIMIT = 50;
const PERSONA_SKIP_REASON = 'persona-not-authorized-for-school-module';
const RUN_REPORTS_DEFAULT_TEMPLATE_ID = 'aging-report';
const RUN_REPORTS_DEFAULT_SELECTED_ENTITIES = ['installments', 'families'] as const;
const RUN_REPORTS_DEFAULT_SELECTED_FIELDS = [
  'family_name',
  'due_date',
  'amount_due',
  'balance_remaining',
  'is_overdue',
  'academic_year',
  'amount_paid',
  'payment_status',
  'plan_name',
] as const;

export const buildRunReportsDefaultReportDefinition = (): AssistantReportDefinition => ({
  title: 'Aging Report',
  pathname: RUN_REPORTS_PATHNAME,
  module: RUN_REPORTS_ROUTE_FAMILY,
  routeFamily: RUN_REPORTS_ROUTE_FAMILY,
  templateId: RUN_REPORTS_DEFAULT_TEMPLATE_ID,
  templateSource: 'gallery',
  selectedEntities: [...RUN_REPORTS_DEFAULT_SELECTED_ENTITIES],
  selectedFields: [...RUN_REPORTS_DEFAULT_SELECTED_FIELDS],
  conditions: [],
  dateRange: {
    preset: 'This Year',
    from: null,
    to: null,
  },
  sorts: [],
});

const toArgs = <Args>(
  request: RunReportsDomainQueryRequest,
  mapRequest: ((request: RunReportsDomainQueryRequest) => Args) | undefined,
): Args => (mapRequest ? mapRequest(request) : request as unknown as Args);

const endpointId = (prefix: string, operation: RunReportsDomainQueryOperation): string =>
  `${prefix}:${operation}`;

const buildRunReportsWorkspaceArgs = <Args>(
  listLimit: number,
  previewLimit: number,
  mapRequest: ((request: RunReportsDomainQueryRequest) => Args) | undefined,
): Args => toArgs({
  operation: 'getAssistantReportGenerationWorkspace',
  args: {
    input: {
      pathname: RUN_REPORTS_PATHNAME,
      module: RUN_REPORTS_ROUTE_FAMILY,
      reportDefinition: buildRunReportsDefaultReportDefinition(),
      previewLimit,
      templateLimit: listLimit,
      scheduleLimit: listLimit,
      workItemLimit: listLimit,
      includeSharedTemplates: true,
      includeArchivedTemplates: false,
      includeDisabledSchedules: true,
      includeClosedWorkItems: true,
    },
  },
}, mapRequest);

export const buildRunReportsPreloadOperations = <
  Context extends RtkPreloadRuntimeContext,
  Args,
  State,
>({
  endpoint,
  endpointIdPrefix,
  enabled,
  readRouteParam,
  pageSize,
  listLimit = DEFAULT_LIST_LIMIT,
  previewLimit = DEFAULT_PREVIEW_LIMIT,
  eagerCachePolicy,
  mapRequest,
}: BuildRunReportsPreloadOperationsOptions<Context, Args, State>): ReadonlyArray<
  RtkPreloadOperationDescriptor<Context, Args, State>
> => [
  defineRtkPreloadOperation<Context, Args, State>({
    moduleId: RUN_REPORTS_MODULE_ID,
    operationId: 'run-reports.getAssistantReportGenerationWorkspace.bootstrap',
    endpointId: endpointId(endpointIdPrefix, 'getAssistantReportGenerationWorkspace'),
    phase: 'eager',
    endpoint,
    buildArgs: () => buildRunReportsWorkspaceArgs(listLimit, previewLimit, mapRequest),
    pagination: { mode: 'cursor', eagerPageLimit: 1, pageSize, inlineContinuation: true },
    criticality: 'critical',
    cachePolicy: eagerCachePolicy,
    enabled,
    skipReason: PERSONA_SKIP_REASON,
  }),
  defineRtkPreloadOperation<Context, Args, State>({
    moduleId: RUN_REPORTS_MODULE_ID,
    operationId: 'run-reports.listAssistantWorkItems.nextPage',
    endpointId: endpointId(endpointIdPrefix, 'listAssistantWorkItems'),
    phase: 'pagination',
    endpoint,
    buildArgs: (context) => {
      const nextToken = readRouteParam(context, 'nextToken');
      return nextToken
        ? toArgs({
            operation: 'listAssistantWorkItems',
            args: {
              input: {
                includeClosed: true,
                module: RUN_REPORTS_ROUTE_FAMILY,
                pathname: RUN_REPORTS_PATHNAME,
                limit: listLimit,
                nextToken,
              },
            },
          }, mapRequest)
        : null;
    },
    pagination: { mode: 'cursor', eagerPageLimit: 1, pageSize, inlineContinuation: true },
    criticality: 'non-critical',
    enabled,
    skipReason: 'inline-pagination-token-required',
  }),
];
